import React, { useEffect, useState } from "react";
import useERPStore from "../../../store/builders/useERPStore";
import {
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Wallet,
  RefreshCw,
  PieChart,
  Briefcase,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Calendar
} from "lucide-react";

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN")}`;

const StatCard = ({ label, value, icon: Icon, color, sub, trend }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm transition-all hover:shadow-md">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color.replace('bg-gradient-to-br from-', 'bg-').split(' to-')[0]} bg-opacity-10`}>
        <Icon size={20} className={color.replace('bg-gradient-to-br from-', 'text-').split(' to-')[0]} />
      </div>
      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{label}</p>
    </div>
    <div>
      <p className="text-lg font-bold text-gray-800 tabular-nums tracking-tight">{value}</p>
      <div className="flex items-center justify-between mt-1">
        {sub && <p className="text-[10px] text-gray-400 font-medium">{sub}</p>}
        {trend && (
          <div className={`flex items-center gap-0.5 text-[10px] font-bold ${trend === 'up' ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {trend === 'up' ? 'Profit' : 'Loss'}
          </div>
        )}
      </div>
    </div>
  </div >
);

const STATUS_FILTERS = [
  { key: "active", label: "Live", dotColor: "bg-emerald-500" },
  { key: "hold", label: "Hold", dotColor: "bg-amber-400" },
  { key: "completed", label: "Completed", dotColor: "bg-blue-500" },
];

const FinanceTab = () => {
  const { financeSummary, isLoading, fetchFinanceSummary } = useERPStore();
  const [financialYear, setFinancialYear] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    if (financialYear === "all") {
      fetchFinanceSummary();
    } else {
      const startYear = parseInt(financialYear);
      // Indian Financial Year: April 1 to March 31 of next year
      fetchFinanceSummary({
        dateFrom: `${startYear}-04-01`,
        dateTo: `${startYear + 1}-03-31`
      });
    }
  }, [fetchFinanceSummary, financialYear]);

  const allProjects = financeSummary?.projects || [];

  const projects = allProjects.filter(p => (p.project_status || "").toLowerCase() === statusFilter);

  const sumField = (list, key) => list.reduce((s, p) => s + (parseFloat(p[key]) || 0), 0);

  // Filter summary + top three cards = sum of currently visible (status-filtered) rows only
  const filterBudget = sumField(projects, "project_budget");
  const filterExpenses = sumField(projects, "total_expenses");
  const filterProfit = sumField(projects, "profit_loss");

  const countByStatus = (key) => allProjects.filter(p => (p.project_status || "").toLowerCase() === key).length;

  const completedProjects = allProjects.filter(p => (p.project_status || "").toLowerCase() === "completed");
  const totalCompletedValue = sumField(completedProjects, "project_budget");
  const totalCompletedProfit = sumField(completedProjects, "profit_loss");

  // FY Options: current year and past 2 years (simplified)
  const currentYear = new Date().getFullYear();
  const fyOptions = [
    { label: "All Time", value: "all" },
    { label: `FY ${currentYear}-${(currentYear + 1).toString().slice(-2)}`, value: currentYear.toString() },
    { label: `FY ${currentYear - 1}-${currentYear.toString().slice(-2)}`, value: (currentYear - 1).toString() },
    { label: `FY ${currentYear - 2}-${(currentYear - 1).toString().slice(-2)}`, value: (currentYear - 2).toString() },
  ];

  return (
    <div className="space-y-4">
      {/* Filters Row */}
      <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-blue-600" />
          <h4 className="text-sm font-bold text-gray-700">Financial Year Filter</h4>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium bg-gray-50 cursor-pointer"
          >
            {fyOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <button
            onClick={() => fetchFinanceSummary(financialYear === "all" ? {} : {
              dateFrom: `${parseInt(financialYear)}-04-01`,
              dateTo: `${parseInt(financialYear) + 1}-03-31`
            })}
            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
            title="Reload Data"
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          <p className="text-sm font-medium text-gray-400">Loading financial data...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards — first three from filtered rows; others are completed-only */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Total Project Value" value={fmt(filterBudget)} icon={Briefcase} color="bg-blue-500" sub={`${projects.length} ${STATUS_FILTERS.find(f => f.key === statusFilter)?.label || ""} Project${projects.length !== 1 ? "s" : ""}`} />
            <StatCard label="Expenses" value={fmt(filterExpenses)} icon={Activity} color="bg-rose-500" sub={`${STATUS_FILTERS.find(f => f.key === statusFilter)?.label || "Filtered"} Outflow`} />
            <StatCard label="Profit" value={fmt(filterProfit)} icon={filterProfit >= 0 ? TrendingUp : TrendingDown} color={filterProfit >= 0 ? "bg-emerald-500" : "bg-rose-500"} trend={filterProfit >= 0 ? "up" : "down"} />
            <StatCard label="Realized Revenue" value={fmt(totalCompletedProfit)} icon={Wallet} color="bg-indigo-500" sub={`Profit from ${completedProjects.length} Completed Projects`} />
            <StatCard label="Total Project Value (Completed)" value={fmt(totalCompletedValue)} icon={PieChart} color="bg-amber-500" sub="Inclusive of Expenses" />
          </div>

          {/* Per-Project Breakdown */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50/50 flex-wrap gap-3">
              <h3 className="font-bold text-gray-800 text-base">Project Economic Analysis</h3>
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                {STATUS_FILTERS.map(({ key, label, dotColor }) => {
                  const active = statusFilter === key;
                  const count = countByStatus(key);
                  return (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${active ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {dotColor && <span className={`w-1.5 h-1.5 rounded-full ${dotColor} inline-block`}></span>}
                      {label}
                      <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${active ? "bg-gray-100 text-gray-600" : "bg-white text-gray-400"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {projects.length === 0 ? (
              <div className="p-16 text-center flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-300">
                  <IndianRupee size={24} />
                </div>
                <p className="text-gray-400 font-medium text-sm">No records found for this period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                      <th className="text-left px-4 py-3">Project Name</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Budget</th>
                      <th className="text-right px-4 py-3">Vendor</th>
                      <th className="text-right px-4 py-3">Salary</th>
                      <th className="text-right px-4 py-3">Petty Cash</th>
                      <th className="text-right px-4 py-3 text-gray-700">Total Exp.</th>
                      <th className="text-right px-4 py-3">P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {projects.map((p, i) => {
                      const isProfit = p.profit_loss >= 0;
                      return (
                        <tr key={p.project_id || i} className="hover:bg-gray-50/30 transition-colors">
                          <td className="px-4 py-4">
                            <div className="font-semibold text-gray-800">{p.project_name || `Project #${p.project_id}`}</div>
                            <div className="text-[10px] text-gray-400">ID: {p.project_id}</div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${p.project_status === "completed"
                              ? "bg-emerald-100 text-emerald-700"
                              : p.project_status === "active"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600"
                              }`}>
                              {p.project_status || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right font-medium text-gray-700 tabular-nums">{fmt(p.project_budget)}</td>
                          <td className="px-4 py-4 text-right font-medium text-gray-600 tabular-nums">{fmt(p.vendor_paid)}</td>
                          <td className="px-4 py-4 text-right font-medium text-gray-600 tabular-nums">{fmt(p.salary)}</td>
                          <td className="px-4 py-4 text-right font-medium text-gray-600 tabular-nums">{fmt(p.petty_cash)}</td>
                          <td className="px-4 py-4 text-right font-bold text-gray-800 tabular-nums">{fmt(p.total_expenses)}</td>
                          <td className="px-4 py-4 text-right">
                            <div className={`font-bold tabular-nums ${isProfit ? "text-emerald-600" : "text-rose-600"}`}>
                              {isProfit ? "+" : ""}{fmt(p.profit_loss)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50/50 font-bold border-t border-gray-200">
                    <tr className="text-gray-800">
                      <td colSpan={2} className="px-4 py-4 text-gray-500 uppercase text-[10px]">Filter Summary</td>
                      <td className="px-4 py-4 text-right tabular-nums">{fmt(filterBudget)}</td>
                      <td colSpan={3}></td>
                      <td className="px-4 py-4 text-right tabular-nums border-l border-gray-100">{fmt(filterExpenses)}</td>
                      <td className={`px-4 py-4 text-right tabular-nums ${filterProfit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {filterProfit >= 0 ? "+" : ""}{fmt(filterProfit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 px-4 py-3 bg-gray-50/50 rounded-lg border border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-400">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-200"></span> Legends:</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400"></span> Vendor</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400"></span> Salary</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-400"></span> Petty Cash</span>
          </div>
        </>
      )}
    </div>
  );
};

export default FinanceTab;
